import { useCallback, useEffect, useMemo, useState } from 'react'
import { Eye, List, Trash2 } from 'lucide-react'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { useRemembered } from '../../components/listMemory'
import IconButton from '../../components/IconButton'
import Drawer from '../../components/Drawer'
import FilterBar from '../../components/FilterBar'
import Modal from '../../components/Modal'
import PageHeader from '../../components/PageHeader'
import StatStrip from '../../components/StatStrip'
import Select from '../../components/Select'
import { useToast } from '../../components/toastContext'
import StatusBadge from '../../components/StatusBadge'
import { deleteBatch, listBatchMessages, listBatches } from './systemLogsService'
import { notificationsLogStrings as S } from './strings'
import './systemLogs.css'

const STATE_TONE = {
  Waiting: 'warning',
  Running: 'primary',
  Stopped: 'dark',
  Finished: 'success',
  Failed: 'danger',
  Succeeded: 'success',
}

/** The view builds this select from the NotificationEventStatus enum. */
const STATE_OPTIONS = Object.entries(S.states).map(([value, label]) => ({ value, label }))

const asDate = (iso) => (iso ? iso.slice(0, 10) : '')
const asDateTime = (iso) => (iso ? iso.replace('T', ' ') : '')

/** سجل التنبيهات — Views/NotificationsDetails/Index.cshtml. */
export default function NotificationsLogPage() {
  const toast = useToast()
  const [rows, setRows] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  /* THE TWO FILTERS DECIDE WHICH BATCHES THESE ARE, so they leave the screen with the page:
     the address came back saying `?page=4` and the type that produced it was gone, which is
     page 4 of a different log. `draft` is what the selects show — without it they read as
     empty over a list that is still filtered. */
  const [filters, setFilters] = useRemembered('notifications-log:filters', { type: '', status: '' })
  const [draft, setDraft] = useRemembered('notifications-log:draft', { type: '', status: '' })

  const [openBatch, setOpenBatch] = useState(null)
  const [messages, setMessages] = useState([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [preview, setPreview] = useState(null)

  const [pendingDelete, setPendingDelete] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    listBatches(filters).then((response) => {
      setRows(response.rows)
      setTemplates(response.templates)
      setLoading(false)
    })
  }, [filters])

  useEffect(load, [load])

  useEffect(() => {
    if (!openBatch) return
    setMessagesLoading(true)
    listBatchMessages(openBatch.id).then((response) => {
      setMessages(response.rows)
      setMessagesLoading(false)
    })
  }, [openBatch])

  const confirmDelete = async () => {
    const batch = pendingDelete
    setPendingDelete(null)
    const response = await deleteBatch(batch.id)
    if (response.ok) toast.success(S.deleted)
    else toast.error(S.deleteFailed)
    if (response.ok) load()
  }

  const columns = useMemo(
    () => [
      {
        key: 'typeName',
        header: S.columns.type,
        width: '220px',
        render: (row) => <bdi>{row.typeName}</bdi>,
      },
      {
        key: 'state',
        header: S.columns.status,
        width: '140px',
        value: (row) => S.states[row.state] ?? row.state,
        render: (row) => (
          <StatusBadge tone={STATE_TONE[row.state] ?? 'dark'}>
            {S.states[row.state] ?? row.state}
          </StatusBadge>
        ),
      },
      {
        key: 'scheduledStartDate',
        header: S.columns.createdOn,
        render: (row) => (
          <span className="sl-date" dir="ltr">
            {asDate(row.scheduledStartDate)}
          </span>
        ),
      },
      {
        key: 'startDate',
        header: S.columns.startDate,
        render: (row) => (
          <span className="sl-date" dir="ltr">
            {asDate(row.startDate)}
          </span>
        ),
      },
      {
        key: 'endDate',
        header: S.columns.endDate,
        render: (row) => (
          <span className="sl-date" dir="ltr">
            {asDate(row.endDate)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: <List size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '104px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <span className="sl-row-actions">
            <IconButton icon={List} label={S.details} onClick={() => setOpenBatch(row)} />
            {/* Only a waiting batch carries the delete button. */}
            {row.state === S.deletableState && (
              <IconButton
                icon={Trash2}
                label={S.delete}
                tone="danger"
                onClick={() => setPendingDelete(row)}
              />
            )}
          </span>
        ),
      },
    ],
    [],
  )

  const messageColumns = useMemo(
    () => [
      {
        key: 'status',
        header: S.messageColumns.status,
        width: '140px',
        value: (row) => S.states[row.status] ?? row.status,
        render: (row) => (
          <StatusBadge tone={STATE_TONE[row.status] ?? 'dark'}>
            {S.states[row.status] ?? row.status}
          </StatusBadge>
        ),
      },
      {
        key: 'lastTry',
        header: S.messageColumns.lastStatusDate,
        width: '200px',
        render: (row) => (
          <span className="sl-date" dir="ltr">
            {asDateTime(row.lastTry)}
          </span>
        ),
      },
      {
        key: 'details',
        header: S.messageColumns.details,
        value: (row) => row.details.map((item) => item.receiver).join(' '),
        render: (row) => (
          <span className="sl-channels">
            {row.details.map((item, index) => (
              <span key={index}>
                <b dir="ltr">{item.channel}:</b> <span dir="ltr">{item.receiver}</span>
              </span>
            ))}
          </span>
        ),
      },
      {
        key: 'preview',
        header: <Eye size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '72px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <IconButton icon={Eye} label={S.preview} tone="info" onClick={() => setPreview(row)} />
        ),
      },
    ],
    [],
  )

  /** How the batches went, in the states' own order rather than by size. */
  const stateSpread = useMemo(() => {
    const counts = new Map()
    for (const row of rows) counts.set(row.state, (counts.get(row.state) ?? 0) + 1)
    return Object.keys(S.states)
      .filter((state) => counts.has(state))
      .map((state) => ({ id: state, label: S.states[state], value: counts.get(state) }))
  }, [rows])

  return (
    <>
      <PageHeader title={S.title} count={loading ? undefined : rows.length} />

      {/* Seven hundred batches and no sense of how they went. The spread across
          states is the whole question this log answers, and it was only ever
          available by paging. Counted off the rows already loaded. */}
      {stateSpread.length > 0 && (
        <StatStrip items={stateSpread} label={S.status} variant="band" />
      )}

      {/* The legacy hides this table's own search box and drives filtering from a
          menu; the box stays hidden, but the two filters are inline and every
          applied one stays up as a chip. */}
      <FilterBar
        chips={[
          filters.type !== '' && {
            id: 'type',
            label: S.type,
            value: templates.find((template) => String(template.type) === String(filters.type))?.name ?? '',
            onRemove: () => {
              setDraft((previous) => ({ ...previous, type: '' }))
              setFilters((previous) => ({ ...previous, type: '' }))
            },
          },
          filters.status !== '' && {
            id: 'status',
            label: S.status,
            value: S.states[filters.status] ?? filters.status,
            onRemove: () => {
              setDraft((previous) => ({ ...previous, status: '' }))
              setFilters((previous) => ({ ...previous, status: '' }))
            },
          },
        ].filter(Boolean)}
        resetLabel={S.reset}
        onReset={() => {
          setDraft({ type: '', status: '' })
          setFilters({ type: '', status: '' })
        }}
      >
        <Select
          id="typeFilter"
          value={draft.type}
          onChange={(value) => {
            setDraft((previous) => ({ ...previous, type: value }))
            setFilters((previous) => ({ ...previous, type: value }))
          }}
          placeholder={S.type}
          aria-label={S.type}
          options={templates.map((template) => ({ value: template.type, label: template.name }))}
        />
        <Select
          id="statusFilter"
          value={draft.status}
          onChange={(value) => {
            setDraft((previous) => ({ ...previous, status: value }))
            setFilters((previous) => ({ ...previous, status: value }))
          }}
          placeholder={S.status}
          aria-label={S.status}
          options={STATE_OPTIONS}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        searchable={false}
        controls={false}
        getRowId={(row) => row.id}
        /* The filters live outside the table, so it cannot see the row set change on its own:
           choosing a type kept page 4, which the filtered log may not have. */
        resetKey={JSON.stringify(filters)}
      />

      {/* The batch's recipients — a drawer, so the log stays behind it, and titled
          by the batch rather than by the word `التفاصيل`. */}
      <Drawer
        dialog
        open={!!openBatch}
        title={openBatch?.typeName ?? S.details}
        subtitle={openBatch ? S.states[openBatch.state] : undefined}
        size="lg"
        onClose={() => setOpenBatch(null)}
      >
        <DataTable
          /* A list inside a sheet is opened to answer one question; it starts at the top, and
             the address belongs to the log behind it. */
          stateKey={false}
          columns={messageColumns}
          rows={messages}
          loading={messagesLoading}
          getRowId={(row) => row.id}
        />
      </Drawer>

      {/* Was a raw JSON dump in a <pre> — a developer artefact in an admin panel.
          Same payload, as the definition list it always was. */}
      <Modal
        open={!!preview}
        title={S.messageColumns.details}
        onClose={() => setPreview(null)}
      >
        <dl className="sl-payload">
          {(preview?.details ?? []).map((item, index) => (
            <div key={index} className="sl-payload__row">
              <dt dir="ltr">{item.channel}</dt>
              <dd dir="ltr">{item.receiver}</dd>
            </div>
          ))}
          <div className="sl-payload__row">
            <dt>
              <bdi>{S.messageColumns.status}</bdi>
            </dt>
            <dd>
              <bdi>{preview ? (S.states[preview.status] ?? preview.status) : ''}</bdi>
            </dd>
          </div>
          <div className="sl-payload__row">
            <dt>
              <bdi>{S.messageColumns.lastStatusDate}</bdi>
            </dt>
            <dd dir="ltr">{preview?.lastTry ? preview.lastTry.replace('T', ' ') : ''}</dd>
          </div>
        </dl>
      </Modal>

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        confirmLabel={S.yes}
        cancelLabel={S.no}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

    </>
  )
}
